/**
 * Inline contenteditable field with autosave.
 *
 * Saves after an idle pause or on blur - never on every keystroke - and hands the
 * value to `saveInlineField()` of the surrounding component (Alpine scope chain).
 * A rejected save rolls the visible text back to the last accepted value, so the
 * DOM never shows something the server did not accept.
 *
 * A still virtual open point has no occurrence yet: passing `itemId` instead routes
 * the first accepted value through `adoptOpenPoint()`, which incarnates the point in
 * this meeting and applies the value in one server transaction.
 */
export default ({
  occurrenceId = null,
  itemId = null,
  field,
  idleMs = 10000,
  required = false,
  multiline = false
} = {}) => {
  let timer = null
  let baseline = ''
  let saving = false

  return {
    init() {
      baseline = this.readValue()
    },

    destroy() {
      clearTimeout(timer)
      timer = null
    },

    readValue() {
      // A contenteditable inserts non-breaking spaces; the stored title must not
      // carry them, so they are normalised here. Written as an escape so the
      // character stays visible to a reader and to eslint.
      return (this.$el.innerText ?? '').replace(/\u00a0/g, ' ').trim()
    },

    writeValue(value) {
      if (this.$el.innerText !== value) this.$el.innerText = value
    },

    /**
     * The same field is on screen twice - the meeting editor shows a point in the
     * agenda list and on the beamer stage at once - and only one of the two did
     * the saving. Take over what was stored, so the other copy neither shows a
     * stale heading nor restores one on the next Escape.
     *
     * The field somebody is writing in is left alone: it holds the newer text.
     */
    syncInlineValue(detail = {}) {
      if (detail.occurrenceId !== occurrenceId || detail.field !== field) return
      if (typeof detail.value !== 'string' || this.$el === document.activeElement) return

      baseline = detail.value
      this.writeValue(detail.value)
    },

    inlineInput() {
      clearTimeout(timer)
      timer = setTimeout(() => this.inlineFlush(), idleMs)
    },

    inlineBlur() {
      clearTimeout(timer)
      timer = null
      this.inlineFlush()
    },

    inlineKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault()
        clearTimeout(timer)
        timer = null
        this.writeValue(baseline)
        this.$el.blur()
        return
      }

      if (event.key === 'Enter' && !multiline) {
        event.preventDefault()
        this.$el.blur()
      }
    },

    async inlineFlush() {
      if (saving) return

      const value = this.readValue()
      if (value === baseline) return

      if (required && value === '') {
        this.writeValue(baseline)
        return
      }

      saving = true

      try {
        const accepted = itemId === null
          ? await this.saveInlineField(occurrenceId, field, value)
          : await this.adoptOpenPoint(itemId, field, value)
        if (accepted) baseline = value
        else this.writeValue(baseline)
      } finally {
        saving = false
      }
    }
  }
}
